import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Palette, Shield, Zap, Settings, Bot, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const features = [
  {
    icon: Globe,
    title: 'WordPress vefsíður',
    description: 'Sérsniðnar WordPress vefsíður fyrir öll fyrirtæki.'
  },
  {
    icon: Palette,
    title: 'Sérhönnuð þemu',
    description: 'Einstök hönnun sem endurspeglar þitt vörumerki.'
  },
  {
    icon: Shield,
    title: 'Öryggi og viðhald',
    description: 'Reglulegar uppfærslur og öryggiseftirlit.'
  },
  {
    icon: Zap,
    title: 'Hraðvirkar lausnir',
    description: 'Hámörkun á hraða og afköstum.'
  },
  {
    icon: Settings,
    title: 'Sérsniðnar viðbætur',
    description: 'Þróun á sérsniðnum viðbótum fyrir WordPress.'
  },
  {
    icon: Bot,
    title: 'WooCommerce',
    description: 'Öflug netverslun með WooCommerce.'
  }
];

const packages = [
  {
    name: 'WordPress Express',
    price: '99.950 kr.',
    description: 'Staðlað útlit hannað af Vefsíðu',
    features: [
      'Staðlað WordPress þema',
      'Grunnstilling og uppsetning',
      'Tengingar við samfélagsmiðla',
      'Hraðvirk og notendavæn lausn',
      'SSL vottun',
      '3 síður innifaldar'
    ]
  },
  {
    name: 'WooCommerce Express',
    price: '149.950 kr.',
    description: 'Staðlað netverslunarútlit',
    features: [
      'Staðlað WooCommerce þema',
      'Grunnstilling og uppsetning',
      'Greiðslugáttir',
      'Vörustjórnun',
      'SSL vottun',
      '5 síður innifaldar'
    ]
  }
];

const WordPress = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div 
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl font-bold text-gray-900 mb-6">
              WordPress lausnir fyrir þitt fyrirtæki
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              Við sérhæfum okkur í að skapa öflugar WordPress veflausnir sem 
              skila árangri.
            </p>
            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-[#EA295A] text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-[#d01d4c] transition-all shadow-lg shadow-[#EA295A]/20 hover:shadow-xl hover:shadow-[#EA295A]/30 flex items-center"
                >
                  Fá tilboð
                  <ArrowRight className="ml-2" />
                </motion.button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Features Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              WordPress þjónusta í sérflokki
            </h2>
            <p className="text-xl text-gray-600">
              Við bjóðum upp á heildstæðar WordPress lausnir
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                className="bg-gray-50 rounded-xl p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-[#EA295A]/10 mb-6">
                  <feature.icon className="w-6 h-6 text-[#EA295A]" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* Packages Section */}
      <motion.div 
        className="py-24 bg-gray-50"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Express pakkar
            </h2>
            <p className="text-xl text-gray-600">
              Hagkvæmar og hraðvirkar lausnir fyrir þitt fyrirtæki
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8">
            {packages.map((pkg, index) => (
              <motion.div 
                key={index}
                className="bg-white rounded-xl p-8 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <div className="text-3xl font-bold text-[#EA295A] mb-4">{pkg.price}</div>
                <p className="text-gray-600 mb-6">{pkg.description}</p>
                <ul className="space-y-3 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-center text-gray-600">
                      <ArrowRight className="w-4 h-4 text-[#EA295A] mr-2" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link to="/fa-tilbod">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-[#EA295A] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#d01d4c] transition-all"
                  >
                    Panta núna
                  </motion.button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.div>

      {/* CTA Section */}
      <motion.div 
        className="py-24 bg-white"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#EA295A] to-[#FF6B6B] rounded-2xl p-12 text-center">
            <motion.h2 
              className="text-3xl font-bold text-white mb-6"
            >
              Tilbúin að hefja þitt WordPress verkefni?
            </motion.h2>
            <motion.p 
              className="text-xl text-white/90 mb-8 max-w-2xl mx-auto"
            >
              Láttu okkur hjálpa þér að skapa frábæra WordPress vefsíðu sem 
              skilar árangri
            </motion.p>
            <motion.div>
              <Link to="/fa-tilbod">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-white text-[#EA295A] px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-all shadow-lg"
                >
                  Fá tilboð
                </motion.button>
              </Link>
            </motion.div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default WordPress;